import React, { useState, useEffect } from "react";
import { FaEye } from "react-icons/fa";
import { MdDelete } from "react-icons/md";
import { useOrganizationDocuments, useUploadDocument, useDeleteDocument } from "../../hooks/useUsers.ts";
import { showConfirmation } from "../../services/confirmation.ts";
import { useTheme } from "../../contexts/ThemeContext.tsx";
import { getTheme } from "../../theme.ts";

const url = process.env.REACT_APP_DB_URI;

interface UploadedDoc {
  id: string;
  name: string;
  document: string; // Corrected property name
  organization: string; // Organization ID
  uploaded_at?: string; // Optional if needed
}

const UploadDocuments = () => {
  const [files, setFiles] = useState<File[]>([]);
  const [docName, setDocName] = useState("");
  const [uploadedDocs, setUploadedDocs] = useState<UploadedDoc[]>([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [viewDoc, setViewDoc] = useState<UploadedDoc | null>(null);
  const [loading, setLoading] = useState(true);
  const [progress, setProgress] = useState(0);

  const reawdata = localStorage.getItem("user");
  const cred = JSON.parse(reawdata || "{}");
  const token = cred?.access;

  const { themeName } = useTheme();
  const currentTheme = getTheme(themeName);

  const { data: uploadedDocsData, isLoading: isLoadingDocs, error: docsError } = useOrganizationDocuments(cred?.user?.id, "user");
  const uploadMutation = useUploadDocument();
  const deleteMutation = useDeleteDocument();

  useEffect(() => {
    if (uploadedDocsData) {
      setUploadedDocs((uploadedDocsData as any)?.documents || []);
    }
  }, [uploadedDocsData]);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      setFiles(Array.from(e.target.files));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (docName && files.length > 0) {
      let formData = new FormData();
      formData.append("names", docName);
      files.forEach((file) => {
        formData.append("documents", file);
      });

      try {
        await uploadMutation.mutateAsync({
          formData,
          onUploadProgress: (progressEvent: any) => {
            const percentCompleted = Math.round(
              (progressEvent.loaded * 100) / (progressEvent.total || 1)
            );
            setProgress(percentCompleted);
          }
        }, {
          onSuccess: () => {
            setFiles([]);
            setDocName("");
            setProgress(0);
            setIsModalOpen(false);
          },
          onError: (err) => {
            console.log(err);
          }
        });
      } catch (err) {
        console.log(err);
      }
    }
  };

  const handleDeleteDocument = async (id: string) => {
    showConfirmation(
      'Delete Document',
      'Are you sure you want to delete this document? This action cannot be undone.',
      'Delete',
      'Cancel',
      async () => {
        try {
          await deleteMutation.mutateAsync(id);
          // Refetch handled by invalidation
        } catch (err) {
          console.error("Error deleting document:", err);
        }
      }
    );
  };

  const handleViewDocument = (doc: UploadedDoc) => {
    setViewDoc(doc);
  };

  return (
    <div style={{ minHeight: '100vh', background: `linear-gradient(to bottom right, ${currentTheme.background}, ${currentTheme.surface})`, padding: '1.5rem' }} className="md:p-6">
      {/* Upload Button */}
      <div className="flex items-center justify-end">
        <button
          onClick={() => setIsModalOpen(true)}
          style={{ backgroundColor: currentTheme.primary, color: currentTheme.text }}
          className="px-4 py-2 rounded shadow mb-6 hover:scale-105 transition-all duration-300"
        >
          Upload Documents
        </button>
      </div>

      {isLoadingDocs ? (
        <div className="flex justify-center items-center mt-6">
          <div className="loader rounded-full w-12 h-12 animate-spin" style={{ borderTop: `4px solid ${currentTheme.primary}` }}></div>
          <p className="ml-4" style={{ color: currentTheme.primary }}>Loading documents...</p>
        </div>
      ) : uploadedDocs?.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {uploadedDocs.map((doc, index) => (
            <div
              key={index}
              style={{ backgroundColor: currentTheme.surface, border: `1px solid ${currentTheme.border}` }}
              className="rounded-xl shadow-lg overflow-hidden hover:shadow-xl transition-all transform hover:-translate-y-1"
            >
              <div className="p-4" style={{ backgroundColor: currentTheme.primary }}>
                <h3 className="text-lg font-semibold" style={{ color: currentTheme.text }}>
                  {doc?.name}
                </h3>
              </div>

              <div className="p-4" style={{ backgroundColor: currentTheme.surface }}>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <button
                      onClick={() => handleViewDocument(doc)}
                      style={{ color: currentTheme.primary, backgroundColor: `${currentTheme.primary}10` }}
                      className="transition flex items-center gap-1 px-3 py-1 rounded-full hover:scale-105"
                    >
                      <FaEye size={16} />
                      <span className="text-sm" style={{ color: currentTheme.primary }}>View</span>
                    </button>
                    <button
                      onClick={() => handleDeleteDocument(doc.id)}
                      style={{ color: currentTheme.error, backgroundColor: `${currentTheme.error}10` }}
                      className="transition flex items-center gap-1 px-3 py-1 rounded-full hover:scale-105"
                    >
                      <MdDelete size={16} />
                      <span className="text-sm" style={{ color: currentTheme.error }}>Delete</span>
                    </button>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <p className="text-center mt-10" style={{ color: currentTheme.text }}>No documents uploaded.</p>
      )}

      {/* Upload Modal */}
      {isModalOpen && (
        <div className="fixed z-[100] inset-0 flex items-center justify-center" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div style={{ backgroundColor: currentTheme.surface, border: `2px solid ${currentTheme.primary}` }} className="p-6 rounded-lg shadow-lg w-96">
            <h3 className="text-lg font-bold mb-4" style={{ color: currentTheme.primary }}>
              Upload Documents
            </h3>

            <form onSubmit={handleSubmit}>
              <input
                type="text"
                value={docName}
                onChange={(e) => setDocName(e.target.value)}
                placeholder="Enter document name"
                required
                style={{ borderColor: currentTheme.border, backgroundColor: `${currentTheme.primary}10` }}
                className="p-2 rounded w-full mb-4 focus:outline-none"
              />

              <input
                type="file"
                multiple
                onChange={handleFileChange}
                required
                style={{ color: currentTheme.primary }}
                className="mb-4 w-full"
              />

              {progress > 0 && (
                <div className="w-full rounded-full h-4 mb-4" style={{ backgroundColor: currentTheme.textMuted }}>
                  <div
                    style={{ backgroundColor: currentTheme.primary, width: `${progress}%` }}
                    className="h-4 rounded-full transition-all duration-300"
                  ></div>
                </div>
              )}

              <div className="flex justify-end space-x-2">
                <button
                  type="button"
                  onClick={() => setIsModalOpen(false)}
                  style={{ backgroundColor: currentTheme.error, color: currentTheme.inverseText }}
                  className="px-4 py-2 rounded hover:scale-105 transition-all duration-300"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  style={{ backgroundColor: currentTheme.primary, color: currentTheme.inverseText }}
                  className="px-4 py-2 rounded hover:scale-105 transition-all duration-300"
                >
                  Upload
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* View Document Modal */}
      {viewDoc && (
        <div className="fixed inset-0 flex items-center z-[991] justify-center" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div style={{ backgroundColor: currentTheme.surface, border: `2px solid ${currentTheme.primary}` }} className="p-6 rounded-lg shadow-lg w-96">
            <h3 className="text-lg font-bold mb-4" style={{ color: currentTheme.primary }}>
              Document Preview
            </h3>
            <p className="mb-2" style={{ color: currentTheme.text }}>
              <strong>Document Name:</strong> {viewDoc?.name}
            </p>
            {/* FIX 2: Removed incorrect file list display */}
            <div className="flex flex-col space-y-2">
              <a
                href={`${process.env.REACT_APP_PF}${viewDoc?.document}`}
                target="_blank"
                rel="noopener noreferrer"
                style={{ color: currentTheme.primary }}
                className="hover:underline"
              >
                View File
              </a>
            </div>
            <div className="flex justify-end mt-4">
              <button
                onClick={() => setViewDoc(null)}
                style={{ backgroundColor: currentTheme.primary, color: currentTheme.inverseText }}
                className="px-4 py-2 rounded hover:scale-105 transition-all duration-300"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UploadDocuments;